import { useEffect, useState } from "react";

import isNil from "lodash/isNil";

import { NavTestID } from "@/constants/TestID";
import DropdownMenu from "@/ui/core/menu/DropdownMenu";
import TabButton from "@/ui/core/TabButton";
import { HStack, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

interface UserTabSelectorTabProps {
  badgeCount?: number;
  universalStatusCode?: UniversalStatusCode;
  userId: string;
  userName: string;
}

interface UserTabSelectorProps extends BaseViewProps {
  appUserId: string;
  initialUserId?: string;
  onUserChange: (userId: string) => void;
  tabs: UserTabSelectorTabProps[];
}

/* eslint complexity: ["error", { max: 8}] -- applies to entire file */
const UserTabSelector = ({
  accessibilityLabel,
  appUserId,
  initialUserId,
  onUserChange,
  tabs,
  testID,
}: UserTabSelectorProps) => {
  const initialSelectedUserId = initialUserId || tabs[0]?.userId;
  const [selectedUserId, setSelectedUserId] = useState<string>(
    initialSelectedUserId,
  );
  const [selectedTabIndex, setSelectedTabIndex] = useState<number | undefined>(
    undefined,
  );
  const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);

  useEffect(() => {
    setSelectedUserId(initialSelectedUserId);
  }, [initialSelectedUserId]);

  useEffect(() => {
    const activeIndex = tabs.findIndex((tab) => tab.userId === selectedUserId);
    if (activeIndex !== -1) {
      setSelectedTabIndex(activeIndex);
    }
  }, [selectedUserId, tabs]);

  const handleUserChange = (userId: string) => {
    if (selectedUserId === userId) {
      return;
    }

    setSelectedUserId(userId);
    onUserChange(userId);
  };

  const baseTestID = joinTestIDs(testID, NavTestID.TabSelector);

  // If there are no tabs or there is only one tab and it's the current logged in user, we don't render the component
  if (
    tabs.length === 0 ||
    (tabs.length === 1 && tabs[0].userId === appUserId)
  ) {
    return null;
  }

  const renderTab = ({
    index: _index,
    isDropdownTrigger,
    tab,
  }: {
    index: number;
    isDropdownTrigger: boolean;
    tab: UserTabSelectorTabProps;
  }) => {
    const isSelf = tab.userId === appUserId;
    const isActive = selectedUserId === tab.userId;

    return (
      <TabButton
        accessibilityLabel={`${accessibilityLabel} ${tab.userName}`}
        badgeCount={tab.badgeCount}
        isActive={isActive}
        isDropdown={isDropdownTrigger && isActive}
        key={tab.userId}
        onPress={() => {
          if (isDropdownTrigger && isActive) {
            setIsMenuVisible(!isMenuVisible);
            return;
          }
          handleUserChange(tab.userId);
          setIsMenuVisible(false);
        }}
        testID={joinTestIDs(baseTestID, tab.userId)}
        title={isSelf ? "You" : tab.userName}
      />
    );
  };

  // eslint-disable-next-line max-params
  const renderedTabs = tabs.map((tab, index) => {
    return renderTab({ index, isDropdownTrigger: false, tab });
  });
  const shouldShowDropdown = tabs.length > 2;
  const dropdownTriggerTab = isNil(selectedTabIndex)
    ? null
    : renderTab({
        index: selectedTabIndex,
        isDropdownTrigger: shouldShowDropdown,
        tab: tabs[selectedTabIndex],
      });

  const renderedDropdown = shouldShowDropdown ? (
    <DropdownMenu
      alignment="right"
      handleClose={() => setIsMenuVisible(false)}
      handleOpen={() => setIsMenuVisible(true)}
      isVisible={isMenuVisible}
      paddingHorizontal={18}
      paddingVertical={18}
      trigger={dropdownTriggerTab}
    >
      <VStack
        alignItems="flex-start"
        backgroundColor={ColorToken.NeutralBackgroundPrimary}
        gap={8}
      >
        {renderedTabs}
      </VStack>
    </DropdownMenu>
  ) : (
    <HStack
      accessibilityLabel={accessibilityLabel}
      flexShrink={1}
      flexWrap="wrap"
      gap={8}
      justifyContent="flex-end"
      testID={joinTestIDs(testID, "user-selector")}
    >
      {renderedTabs}
    </HStack>
  );

  return renderedDropdown;
};

export default UserTabSelector;
export type { UserTabSelectorProps, UserTabSelectorTabProps };
